import { receiveNewKeyword } from 'actions/identifier';
import PapaParse from 'papaparse';
import React, { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { LanguageMatcherType, UUID } from 'types';
import { PLUS_ADD_FIELD_ICON } from 'constants/annotatorIcons';
import LanguageMatcherList from '../LanguageMatcherList';

type Props = {
  label: string;
  helperText?: string;
  languageMatcherId: UUID;
  parentType: LanguageMatcherType;
  field: string;
  value: string[];
  widthClassName?: string;
  enableImport?: boolean;
  compareMode?: boolean;
  shouldDisable?: boolean;
  useDisabledColor?: boolean;
};

const extractTermsFromCsv = (rows: Record<string, unknown>[]): string[] => {
  if (!rows || rows.length === 0) return [];
  const first = rows[0] || {};
  const keys = Object.keys(first);
  const likelyKey =
    keys.find((k) => k.toLowerCase() === 'terms') ||
    keys.find((k) => k.toLowerCase() === 'term') ||
    keys[0];
  if (!likelyKey) return [];
  return rows
    .map((r) => r[likelyKey])
    .filter((v): v is string => typeof v === 'string')
    .flatMap((s) => s.split(/\r?\n/))
    .map((s) => s.trim())
    .filter(Boolean);
};

const ListFieldWithImport: React.FC<Props> = ({
  label,
  helperText,
  languageMatcherId,
  parentType,
  field,
  value,
  widthClassName = 'w-124',
  enableImport = true,
  compareMode = false,
  shouldDisable = false,
  useDisabledColor = false,
}) => {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState('');
  const fileInputEl = useRef<HTMLInputElement>(null);

  const terms = useMemo(() => value || [], [value]);

  const commitDraft = (): void => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const next = trimmed
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (next.length === 0) return;

    dispatch(
      receiveNewKeyword({
        languageMatcherId,
        parentType,
        data: {
          key: field,
          value: next,
        },
      })
    );
    dispatch(
      receiveNewKeyword({
        languageMatcherId,
        parentType,
        data: { key: 'saved', value: false },
      })
    );
    setDraft('');
  };

  const handleImportClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation();
    fileInputEl.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const fileReader = new FileReader();
    if (!e.target.files?.[0]) return;
    fileReader.readAsText(e.target.files[0], 'UTF-8');
    fileReader.onload = (event): void => {
      const text = event.target?.result;
      if (typeof text !== 'string') return;
      const parsed = PapaParse.parse(text, { header: true, skipEmptyLines: true });
      const extracted = extractTermsFromCsv(parsed.data as Record<string, unknown>[]);
      if (extracted.length === 0) return;
      dispatch(
        receiveNewKeyword({
          languageMatcherId,
          parentType,
          data: {
            key: field,
            value: extracted,
          },
        })
      );
      dispatch(
        receiveNewKeyword({
          languageMatcherId,
          parentType,
          data: { key: 'saved', value: false },
        })
      );
    };
  };

  return (
    <div className={`flex flex-col gap-1 `}>
      <div className="flex flex-col gap-1">
        <span className="text-body">{label}</span>
        {helperText && <span className="text-small text-litlingo-gray-4">{helperText}</span>}
      </div>

      <div className="flex flex-row items-start gap-2">
        <div className={`${widthClassName}`}>
          <div className="flex">
            <div className="relative flex-grow focus-within:z-10">
              <textarea
                value={draft}
                onChange={(ev): void => setDraft(ev.target.value)}
                rows={1}
                className="form-input block w-full border-r-0 border-litlingo-gray-2 rounded-l-lg rounded-r-none text-body"
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDraft();
                  }
                }}
                onBlur={commitDraft}
                disabled={compareMode || shouldDisable}
              />
            </div>
            <button
              type="button"
              onClick={commitDraft}
              className="flex justify-center items-center border border-l-0 border-litlingo-gray-2 rounded-r-lg bg-litlingo-gray-1 focus:outline-none"
              disabled={compareMode || shouldDisable}
            >
              <span className="px-1.5">{PLUS_ADD_FIELD_ICON}</span>
            </button>
          </div>
        </div>

        {enableImport && (
          <div className="shrink-0">
            <input
              ref={fileInputEl}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ position: 'fixed', top: '-100em' }}
            />
            <button
              type="button"
              onClick={handleImportClick}
              className="button button--secondary h-9 w-20 justify-center"
              disabled={compareMode || shouldDisable}
            >
              <span className="whitespace-no-wrap font-bold">Import</span>
            </button>
          </div>
        )}
      </div>

      {terms.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {terms.map((t) => (
            <LanguageMatcherList
              key={t}
              id={languageMatcherId}
              keyword={t}
              type={field}
              useDisabledColor={useDisabledColor}
              compareMode={compareMode}
              shouldDisable={shouldDisable}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ListFieldWithImport;
